import React from 'react';

import moment from 'moment';
import { Grid, IconButton, Typography } from '@material-ui/core';

import BooleanRuleDataFormComponent from 'features/wells/components/BooleanRuleDataFormComponent';
import GeographyRuleDataFormComponent from 'features/wells/components/GeographyRuleDataFormComponent';
import NumericRuleDataFormComponent from 'features/wells/components/NumericRuleDataFormComponent';
import SelectPicklistOptionComponent from 'features/wells/components/SelectPicklistOptionComponent';
import TimeRuleDataFormComponent from 'features/wells/components/TimeRuleDataFormComponent';

import { Formik } from 'formik';

import {
  IntegrityForm as RuleFormFields,
  RULE_TYPES_ENUM,
} from 'features/settings/integrityManagement/helpers/integrityManagement.constants';

import * as Yup from 'yup';
import { isNil } from 'lodash';
import { useDispatch } from 'react-redux';
import { addNewIntegrityManagementWellData } from 'features/settings/integrityManagement/helpers/integrityManagement.actions';

import EditIcon from '@material-ui/icons/Create';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import { withStyles } from '@material-ui/core/styles';

const WellTableValueComponent = ({
  classes,
  wellboreId,
  integrity,
  currentlyEditingId,
  setCurrentlyEditingId,
}) => {
  const dispatch = useDispatch();
  const isEditing = currentlyEditingId === integrity.integrityId;

  const getXsValue = (ruleType) => {
    switch (ruleType) {
      case RULE_TYPES_ENUM.RULE_PICKLIST:
        return 11;
      case RULE_TYPES_ENUM.RULE_TIME:
        return 11;
      default:
        return 7;
    }
  };

  const xsValue = getXsValue(integrity.integrityDetailType);
  const updateValue = (values) => {
    dispatch(addNewIntegrityManagementWellData(wellboreId, values, () => {}));
  };

  const FormSchema = Yup.object().shape({
    [RuleFormFields.VALUE]: Yup.mixed()
      .nullable()
      .when(RuleFormFields.RULE_TYPE, {
        is: RULE_TYPES_ENUM.RULE_DOUBLE,
        then: Yup.number().required('Numeric value is required'),
        otherwise: Yup.mixed().when(RuleFormFields.RULE_TYPE, {
          is: RULE_TYPES_ENUM.RULE_GEOGRAPHY,
          then: Yup.object({
            x: Yup.number().required('Longitude is required'),
            y: Yup.number().required('Latitude is required'),
          }).nullable(),
          otherwise: Yup.mixed().when(RuleFormFields.RULE_TYPE, {
            is: RULE_TYPES_ENUM.PICK_LIST,
            then: Yup.number().required('Pick list selection is required'),
            otherwise: Yup.string().required('Required').nullable(),
          }),
        }),
      }),
  });

  const displayValueByType = () => {
    if (integrity.integrityDetailType === RULE_TYPES_ENUM.RULE_GEOGRAPHY) {
      return `${integrity?.value?.x}, ${integrity?.value?.y}`;
    }
    if (integrity.integrityDetailType === RULE_TYPES_ENUM.RULE_BOOL) {
      if (integrity.value === true) {
        return 'Yes';
      }
      if (integrity.value === false) {
        return 'No';
      }
    }
    if (integrity.integrityDetailType === RULE_TYPES_ENUM.RULE_PICKLIST) {
      if (integrity.details && Array.isArray(integrity.details)) {
        const selectedId = integrity.value.id;
        const selectedDetail = integrity.details.find(
          (detail) => detail.integrityDetailId === selectedId,
        );
        if (selectedDetail) {
          return selectedDetail?.title;
        }
        return null;
      }
    }
    if (integrity.integrityDetailType === RULE_TYPES_ENUM.RULE_TIME) {
      return integrity.value
        ? moment(integrity.value).format('YYYY-MM-DD HH:mm')
        : '';
    }
    return integrity.value !== undefined && integrity.value !== null
      ? integrity.value.toString()
      : '';
  };

  const displayValue = displayValueByType();

  const initialFormValues = {
    [RuleFormFields.TITLE]: integrity.title,
    [RuleFormFields.INTEGRITY_ID]: integrity.integrityId,
    [RuleFormFields.RULE_TYPE]: integrity.integrityDetailType,
    [RuleFormFields.DEFAULT_VALUE_CHECKED]: true,
    [RuleFormFields.VALUE]:
      integrity.integrityDetailType === RULE_TYPES_ENUM.RULE_PICKLIST
        ? integrity.value?.id
        : integrity.integrityDetailType === RULE_TYPES_ENUM.RULE_GEOGRAPHY
        ? { x: integrity.value.x, y: integrity.value.y }
        : integrity.integrityDetailType === RULE_TYPES_ENUM.RULE_BOOL
        ? isNil(integrity.value)
          ? null
          : Number(integrity.value)
        : integrity.value,
    [RuleFormFields.PICKLIST_OPTIONS]: integrity.details,
  };

  const ruleTypeComponents = {
    [RULE_TYPES_ENUM.RULE_BOOL]: BooleanRuleDataFormComponent,
    [RULE_TYPES_ENUM.RULE_DOUBLE]: NumericRuleDataFormComponent,
    [RULE_TYPES_ENUM.RULE_GEOGRAPHY]: GeographyRuleDataFormComponent,
    [RULE_TYPES_ENUM.RULE_TIME]: TimeRuleDataFormComponent,
    [RULE_TYPES_ENUM.RULE_PICKLIST]: SelectPicklistOptionComponent,
  };

  const SelectedComponent =
    ruleTypeComponents[initialFormValues[RuleFormFields.RULE_TYPE]] ||
    (() => null);

  if (!isEditing) {
    return (
      <Grid container alignItems="center" className={classes.viewContainer}>
        <Grid item className={classes.viewTextContainer}>
          <Typography className={classes.text}>{displayValue}</Typography>
        </Grid>
        <Grid item className={classes.editIconContainer}>
          <IconButton
            onClick={() => {
              setCurrentlyEditingId(integrity.integrityId);
            }}
            size="small"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Grid>
      </Grid>
    );
  }

  return (
    <Formik
      initialValues={initialFormValues}
      validationSchema={FormSchema}
      onSubmit={(values, actions) => {
        const payload =
          values.integrityDetailType === RULE_TYPES_ENUM.RULE_GEOGRAPHY // for geography
            ? {
                ...values,
                value: {
                  longitude: values.value.y,
                  latitude: values.value.x,
                  srid: 4326,
                },
              }
            : values; // for other rule types

        updateValue(payload);
        setCurrentlyEditingId(null);
        actions.setSubmitting(false);
      }}
    >
      {({
        handleSubmit,
        resetForm,
        dirty,
        submitForm,
        setFieldValue,
        values,
      }) => (
        <form onSubmit={handleSubmit}>
          <Grid container alignItems="center" className={classes.formContainer}>
            <Grid item className={classes.fieldContainer}>
              <SelectedComponent
                updateValue={() => {}}
                integrity={integrity}
                manualSave={true}
                xs={xsValue}
                paddingTop={0}
              />
            </Grid>
            <Grid item className={classes.actionButtonsContainer}>
              <IconButton type="submit" size="small" disabled={!dirty}>
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  resetForm();
                  setCurrentlyEditingId(null);
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};

const styles = (theme) => ({
  root: {
    width: '100%',
  },
  viewContainer: {
    width: '100%',
  },
  viewTextContainer: {
    width: '90%',
  },
  text: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  editIconContainer: {
    width: '10%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  formContainer: {
    width: '100%',
  },
  fieldContainer: {
    width: '80%',
  },
  actionButtonsContainer: {
    width: '20%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
  },
});

export default withStyles(styles)(WellTableValueComponent);
